/**
 * @file WorkspaceTablet.tsx
 * @description Рабочая область конструктора 768–1279 (uiux-builder-shell.md
 * §2.3, §9): слева навигатор или открытая панель (одна «←» — к структуре),
 * справа канва всегда. «←» адрес не меняет, поэтому несохранённый блок
 * спрашивается явно (`leave` — то же окно «Блок не сохранён», QA 768).
 * Левая колонка тянется ручкой (320–480) и сворачивается в полоску: кнопка
 * «Свернуть» — в шапке панели или в строке с кнопкой настроек (48 px под палец).
 */

import { SlidersHorizontal } from "lucide-react";

import { Button } from "../../ui/button";
import { useBuilder } from "../builder-context";
import { BuilderNav } from "../nav/BuilderNav";
import { BuilderPanel } from "../panels/BuilderPanel";
import { CanvasColumn } from "./CanvasColumn";
import { CollapseButton, CollapsedStrip, ColumnHandle, HANDLE_LABELS, type ColumnsApi } from "./column-controls";
import { settingsButtonLabel } from "../../../lib/site-builder/panels";
import { cn } from "@/lib/utils";

interface Props {
  api: ColumnsApi;
  /** Слева открыта панель (иначе навигатор). */
  leftPanel: boolean;
  onLeftPanel: (open: boolean) => void;
  heightClass: string;
}

export function WorkspaceTablet({ api, leftPanel, onLeftPanel, heightClass }: Props) {
  const b = useBuilder();
  const { widths } = api;
  const side = widths.columns.side;
  const what = leftPanel ? "панель настроек" : "структуру сайта";
  const collapse = <CollapseButton side="left" label={`Свернуть ${what}`} onClick={() => api.collapse("side")} />;

  return (
    <div ref={widths.containerRef} style={widths.style} data-testid="builder-workspace" className={cn("grid min-h-0", heightClass)}>
      {side?.collapsed ? (
        <CollapsedStrip
          side="left"
          label={`Показать ${what}`}
          caption={leftPanel ? "Настройки" : "Структура сайта"}
          minWidth={side.min}
          autoFocus={api.focusKey === "side"}
          onExpand={(w) => widths.expand("side", w)}
        />
      ) : (
        <>
          <aside aria-label="Структура и настройки" className="flex min-h-0 flex-col overflow-y-auto overscroll-contain p-3">
            {leftPanel ? (
              // Одна «←» в шапке панели — назад к структуре (выбор остаётся на канве).
              <BuilderPanel
                className="flex-1"
                headerExtra={collapse}
                onBack={() => {
                  void b.leave().then((ok) => {
                    if (ok) onLeftPanel(false);
                  });
                }}
              />
            ) : (
              <>
                <div className="mb-3 flex items-center justify-between gap-2">
                  <Button variant="outline" onClick={() => onLeftPanel(true)}>
                    <SlidersHorizontal aria-hidden />
                    {settingsButtonLabel(b.selection.screen)}
                  </Button>
                  {collapse}
                </div>
                <BuilderNav />
              </>
            )}
          </aside>
          <ColumnHandle api={api} column="side" label={leftPanel ? HANDLE_LABELS.panel : HANDLE_LABELS.nav} />
        </>
      )}
      <CanvasColumn />
    </div>
  );
}
